import React from 'react';

import { Box, Divider, Flex } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { OKRCreationSecondaryPanel } from 'components/RabbitOKRCreationPanel/SecondaryPanel';
import { OtherDetailsFooter } from 'components/RabbitOKRSidePanel/SecondaryPanel/OtherDetailsPanel/sections/OtherDetailsFooter';
import { userParentOKRSelection } from 'components/RabbitOKRSidePanel/SecondaryPanel/ParentOKRSelectionPanel/hooks/userParentOKRSelection';
import { RealignFooterV2 } from 'components/RabbitOKRSidePanel/SecondaryPanel/RealignPanel/RealignFooterV2';
import { SecondaryPanel } from 'components/RabbitOKRSidePanel/SecondaryPanel/SecondaryPanel';
import {
  closeAddEditCustomTagsPanel,
  closeRealignPanel,
  closeTitleAndMetricsSuggestionPanel,
  SECONDARY_PANEL_TYPES,
} from 'components/RabbitOKRSidePanel/krDrawerSecondaryPanelSlice';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { kRDrawerSecondaryPanelSelector } from 'selectors/krSecondaryPanel';
import { colors } from 'themes/tokens';

import { CollapseSidepanel } from 'assets/assetsV1/iconComponents';

import { modifyString, StringModifier } from 'constants/copyHelpers';
import { isMobileView } from 'constants/isMobileView';

import {
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  primaryDrawerWidth,
  secondaryDrawerWidth,
} from 'uiToolkitV1/BottomDrawer';
import { Button } from 'uiToolkitV1/Button';
import { TextBodyLarge } from 'uiToolkitV1/Typography';

import OKRCreationPrimaryPanelV2 from './OKRCreationPrimaryPanelV2';

interface Props {
  onClose: () => void;
  header: React.ReactNode;
  children: React.ReactNode;
  onSave: () => void;
  onObjectiveCreation: () => void;
  onKeyResultCreation: () => void;
  isOpenAsParent?: boolean;
}

const StyledBody = styled(DrawerBody)`
  width: 100%;
  flex: 1;
  display: flex;
`;

const StyledCollapseSidepanel = styled(CollapseSidepanel)`
  color: #666666 !important;
`;

const SecondaryPanelHeader = ({ SECONDARY_PANEL_ACTION_MAP, secondaryPanelType, isDisable = false }) => (
  <>
    {SECONDARY_PANEL_ACTION_MAP[secondaryPanelType]?.isReplaceHeader ? null : (
      <Box
        display="flex"
        w="100%"
        p="12px"
        alignItems="center"
        sx={{
          button: {
            height: '20px',
            width: '20px',
          },
        }}
        pointerEvents={isDisable ? 'none' : 'all'}
        opacity={isDisable ? '0.5' : 1}
      >
        <TextBodyLarge bold mr="auto" fontSize="14px" lineHeight="20px">
          {SECONDARY_PANEL_ACTION_MAP[secondaryPanelType]?.heading}
        </TextBodyLarge>
        <Button
          StartIcon={StyledCollapseSidepanel}
          variant="tertiary"
          onClick={() => {
            // @ts-ignore
            SECONDARY_PANEL_ACTION_MAP[secondaryPanelType]?.onClose();
          }}
        />
      </Box>
    )}
    {/* @ts-ignore */}
    {SECONDARY_PANEL_ACTION_MAP[secondaryPanelType]?.isReplaceHeader ? null : (
      <Divider
        opacity={1}
        borderColor="#CFD7DF"
        mt={isMobileView() ? '2px' : '8px'}
        mb={isMobileView() ? '2px' : '12px'}
        marginLeft="auto"
        marginRight="auto"
        width="95%"
      />
    )}
  </>
);

const Layout = ({
  onClose,
  header,
  onSave,
  onObjectiveCreation,
  onKeyResultCreation,
  isOpenAsParent = false,
}: Props) => {
  const { type: secondaryPanelType, details } = useSelector(kRDrawerSecondaryPanelSelector);
  const parentPanelDetails = details.parentState;

  const { moduleName } = useOrganisationSettings();
  const dispatch = useDispatch();

  const { localSelectedTimePeriod, selectedParent, setLocalSelectedTimePeriod, setSelectedParent } =
    userParentOKRSelection();

  const showSecondaryPanel = secondaryPanelType !== null;

  const showOnMobileView = !(showSecondaryPanel && isMobileView());

  const isChildrenRealignPanel = secondaryPanelType === SECONDARY_PANEL_TYPES.REALIGN_CHILDREN;
  const isDeletionChildrenRealignPanel = secondaryPanelType === SECONDARY_PANEL_TYPES.REALIGN_CHILDREN_AND_DELETE;

  const realignHeaderText =
    isChildrenRealignPanel || isDeletionChildrenRealignPanel
      ? `Re-align Child ${modifyString(moduleName, StringModifier.PLURAL_TITLE_CASE)} for`
      : `Re-align Parent ${moduleName} for`;

  const SECONDARY_PANEL_ACTION_MAP = {
    [SECONDARY_PANEL_TYPES.OTHER_DETAILS_PANEL]: {
      isReplaceHeader: true,
      isReplaceFooter: true,
    },
    [SECONDARY_PANEL_TYPES.REALIGN_OKR]: {
      onClose: () => {
        // @ts-ignore
        dispatch(closeRealignPanel());
      },
      heading: realignHeaderText,
      Footer: OtherDetailsFooter,
    },
    [SECONDARY_PANEL_TYPES.SELECT_PARENT_OKR]: {
      onClose: () => {
        // @ts-ignore

        dispatch(closeRealignPanel());
      },
      heading: `Align a parent ${modifyString(moduleName, StringModifier.TITLE_CASE)}`,
      Footer: (props) => (
        <RealignFooterV2
          selectedParent={selectedParent}
          setSelectedParent={setSelectedParent}
          localSelectedTimePeriod={localSelectedTimePeriod}
          setLocalSelectedTimePeriod={setLocalSelectedTimePeriod}
          {...props}
        />
      ),
    },
    [SECONDARY_PANEL_TYPES.TITLE_AND_METRICS_SUGGESTION_PANEL]: {
      onClose: () => {
        // @ts-ignore
        dispatch(closeTitleAndMetricsSuggestionPanel());
      },
      isReplaceHeader: true,
      isReplaceFooter: true,
    },
    [SECONDARY_PANEL_TYPES.ADD_EDIT_CUSTOM_TAGS]: {
      onClose: () => {
        // @ts-ignore
        dispatch(closeAddEditCustomTagsPanel());
      },
      isReplaceHeader: true,
      isReplaceFooter: true,
    },
  };

  const Footer = SECONDARY_PANEL_ACTION_MAP[secondaryPanelType]?.Footer ?? <></>;

  const onSecondaryPanelSave = () => {
    // @ts-ignore
    dispatch(SECONDARY_PANEL_ACTION_MAP[secondaryPanelType]?.onSave());
  };

  return (
    <DrawerContent
      sx={{
        padding: '4px !important',
      }}
      width={{
        base: '100%',
        md: showSecondaryPanel ? `${primaryDrawerWidth + secondaryDrawerWidth}px` : `${primaryDrawerWidth}px`,
      }}
      mr={{
        base: '0 !important',
        md: '32px !important',
      }}
    >
      <Box
        sx={{
          borderRadius: '8px',
          p: 1,
          '.close-button-drawer': {
            base: {
              top: '-19px',
              left: isMobileView() ? '-6px' : '-19px',
            },
          },
          height: '100%',
          display: 'flex',
          width: '100%',
          bg: colors['#EBEFF3'],
        }}
        className="drawer-content-wrapper"
      >
        <Box
          className="first-drawer-wrapper"
          height="100%"
          w={{
            base: showSecondaryPanel ? 0 : '100%',
            md: '100%',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',

              background: showSecondaryPanel ? '#FAFAFA' : '#FFFFFF',
              borderRadius: '8px',
              height: '100%',
            }}
            className="drawer-body-wrapper"
          >
            {/* For close icon on top of bottom drawer outside overflow hidden of panels */}
            <DrawerHeader onClose={onClose} opacity={1}>
              {null}
            </DrawerHeader>
            {isOpenAsParent && (
              <SecondaryPanelHeader
                SECONDARY_PANEL_ACTION_MAP={SECONDARY_PANEL_ACTION_MAP}
                secondaryPanelType={parentPanelDetails?.type}
                isDisable
              />
            )}
            {showOnMobileView && (
              <StyledBody opacity={showSecondaryPanel ? 0.5 : 1} pointerEvents={showSecondaryPanel ? 'none' : 'unset'}>
                {/* @ts-ignore */}
                <Flex w="100%">
                  {isOpenAsParent ? (
                    // @ts-ignore
                    <SecondaryPanel isOpenAsParent isOKRCreationPanel />
                  ) : (
                    <OKRCreationPrimaryPanelV2
                      isOpenAsParent={isOpenAsParent}
                      onClose={onClose}
                      showSecondaryPanel={showSecondaryPanel}
                      onObjectiveCreation={onObjectiveCreation}
                      onKeyResultCreation={onKeyResultCreation}
                    />
                  )}
                </Flex>
              </StyledBody>
            )}
          </Box>
        </Box>
        {showSecondaryPanel && (
          <Box
            className="second-drawer-wrapper"
            height="100%"
            w="100%"
            overflow="hidden"
            boxShadow="0px 2px 8px 0px rgba(0, 0, 0, 0.08)"
            bgColor="white"
          >
            <Box
              sx={{
                padding:
                  secondaryPanelType && SECONDARY_PANEL_ACTION_MAP[secondaryPanelType]?.isReplaceFooter ? '0px' : '8px',
                display: 'flex',
                flexDirection: 'column',
                height:
                  secondaryPanelType && SECONDARY_PANEL_ACTION_MAP[secondaryPanelType]?.isReplaceFooter
                    ? '100%'
                    : 'calc(100% - 72px)',
              }}
              className="drawer-body-wrapper"
            >
              <SecondaryPanelHeader
                SECONDARY_PANEL_ACTION_MAP={SECONDARY_PANEL_ACTION_MAP}
                secondaryPanelType={secondaryPanelType}
              />
              <Box h="100%" borderRadius="0px 8px 8px 0px" overflow="hidden">
                {/* @ts-ignore */}
                {showSecondaryPanel ? (
                  // @ts-ignore
                  <OKRCreationSecondaryPanel
                    renderWithoutWrapper
                    selectedParent={selectedParent}
                    setSelectedParent={setSelectedParent}
                    localSelectedTimePeriod={localSelectedTimePeriod}
                    setLocalSelectedTimePeriod={setLocalSelectedTimePeriod}
                  />
                ) : null}
              </Box>
            </Box>
            {!SECONDARY_PANEL_ACTION_MAP[secondaryPanelType]?.isReplaceFooter && (
              <DrawerFooter h="72px" backgroundColor="neutral.F5F7F9">
                {/* @ts-ignore */}
                <Footer
                  onCloseSecondaryPanel={() => {
                    // @ts-ignore
                    SECONDARY_PANEL_ACTION_MAP[secondaryPanelType]?.onClose();
                  }}
                  onSave={onSecondaryPanelSave}
                  // @ts-ignore
                  onTertiaryClick={() => {}}
                />
              </DrawerFooter>
            )}
          </Box>
        )}
      </Box>
    </DrawerContent>
  );
};

export default Layout;
